import { Component, type OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '../../../service/events/dashboard.service';
import { ClientesApiService } from '../../../service/api/clientes-api.service';
import { InputType, type ItemInterface } from '../../../common/components/form-builder/form-builder.component';
import { RequestButtonTypes } from '../../../common/components/request-button/request-button.component';
import { RequestHandler } from '../../../service/OffService/request-handler';
import { BaseView } from '../../base-view';
import { StorageManager } from '../../../common/storage-manager.class';
import { environment } from '../../../../environments/environment';
import { SdiApiService } from '../../../service/api/sdi.service';
import { createCsv } from '../../../common/components/reactive-form-builder/export/createCsv';
import { ClienteModel } from '../../../models/cliente.model';
import { ColInterface } from '../../../common/components/common-list-table/col.interface';
import { ColStylesInterface } from '../../../common/components/common-list-table/col-styles.interface';
import { TreeNode } from 'primeng/api';
import { ClientesEnvironmentDefinition } from './environment-variables';
import { ClientesFormDefinition } from './clientes-form/form-definition';
import { FormType } from '../../../common/components/reactive-form-builder/interfaces/form.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Filtering } from '../../../service/filtering/filtering';

@Component({
  selector: 'app-clientes',
  standalone: false,
  templateUrl: './clientes.component.html',
  styleUrls: ['./clientes.component.scss']
})
export class ClientesComponent extends BaseView<ClienteModel> implements OnInit {
  /** Reactive form definition used for modal/detail/export contexts */
  public form!: ClientesFormDefinition;

  
  /** Environment flags and app-level toggles for this view */
  public readonly env: ClientesEnvironmentDefinition = new ClientesEnvironmentDefinition();

  public filter: Filtering<ClienteModel> = new Filtering<ClienteModel>();;

  
  /** Handlers wired into BaseView for list refresh / deletion */
  public readonly getRequest!: RequestHandler<ClienteModel[]>;
  
  public readonly getRequestSdi!: RequestHandler<ClienteModel[]>;
  
  public readonly deleteRequest!: RequestHandler<ClienteModel>;

  
  /** Currently selected client kept in storage for cross-view usage */
  public currentClient: ClienteModel = StorageManager.getClient() ?? ({} as ClienteModel);

  
  /** Delete prompts (message + confirmation flag/text) */
  public deleteWarningMessage = '';
  
  public deleteWarning = '';

  
  /** Logged user (used for filtering/visibility rules) */
  public readonly user = StorageManager.getUser();

  
  /** Delete button style/type used by shared components */
  public readonly deleteRequestButtonType: RequestButtonTypes = RequestButtonTypes.DELETE;

  
  /** Date range inputs used by CSV/Reports generation */
  public readonly generarFields: ItemInterface<object>[] = [
    { field: 'fecha_inicio', label: 'Fecha inicio', inputType: { type: InputType.CALENDAR } },
    { field: 'fecha_fin', label: 'Fecha fin', inputType: { type: InputType.CALENDAR } }
  ];

  
  /** Table columns for internal (main) dataset */
  public readonly cols: ColInterface[] = [
    { field: 'nombre', header: 'Nombre o Razón Social', filter: '', width: '200px' },
    { field: 'apellido_1', header: 'Apellido' },
    { field: 'direccion', header: 'Dirección', filter: '', visible: this.env.appName !== 'avafitogest' },
    { field: 'nif', header: 'NIF\u00A0/\u00A0CIF', filter: '' },
    { field: 'localidad', header: 'Localidad', filter: '' },
    { field: 'email', header: 'Email', filter: '', width: '200px' },
    { field: 'usuario_asignado_nombre', header: 'Técnico', filter: '', visible: this.env.showTecnicoAsignado },
    { field: 'observaciones', header: 'Observ.', filter: '', visible: this.env.showClientesObservaciones, width: '40px' },
    { field: 'cooperativa', header: 'Cooperativa', visible: this.env.showClientesCooperativa },
    { field: 'tipo', header: 'Tipo', visible: this.env.appName === 'agrosalvi' }
  ];

  /** Table columns for SDI dataset */
  public readonly colsSdi: ColInterface[] = [
    { field: 'nomcli', header: 'Nombre', filter: '', width: '160px' },
    { field: 'razon', header: 'Razón', filter: '', width: '160px' },
    { field: 'nifcli', header: 'NIF', filter: '' },
    { field: 'dircli', header: 'Dirección', filter: '' },
    { field: 'dtocli', header: 'Dto.', filter: '', width: '60px' },
    { field: 'nomprovi', header: 'Provincia', filter: '' },
    { field: 'pobcli', header: 'Población', filter: '' },
    { field: 'telcli', header: 'Teléfono', filter: '' },
    { field: 'email', header: 'Email', filter: '', width: '160px' }
  ];

  /** Lifecycle teardown to avoid memory leaks on subscriptions */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly dashboard: DashboardService,
    public readonly clientesApi: ClientesApiService,
    public readonly sdiApi: SdiApiService
  ) {
    // BaseView wires list loader and deleter into standardized behaviors
    super(dashboard, clientesApi.clientesGET, clientesApi.clientesDELETE);

    this.getRequest = this.clientesApi.clientesGET;
    this.getRequestSdi = this.sdiApi.SdiClientesGET;
    this.deleteRequest = this.clientesApi.clientesDELETE;
  }

  /** Initialize menu state, listen to client change events, fetch lists, and configure filters */
  ngOnInit(): void {
    this.dashboard.setMenuItem('clientes');
    this.dashboard.changeClientActive = true;

    this.dashboard
      .onChangeClient()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentClient = StorageManager.getClient() ?? ({} as ClienteModel);
      });

    this.initFilter();
  }

  /** Persist selected client to storage and notify the app-wide dashboard listeners */
  public selectClient(data: ClienteModel): void {
    StorageManager.saveClient(data);
    this.dashboard.emitClientChange();
  }

  /** Navigate to client creation flow */
  public add(): void {
    this.router.navigate(['dashboard', 'crear-cliente']);
  }

  /** Navigate to client edit flow (keeps backward compatibility with current route schema) */
  public edit(data: ClienteModel): void {
    this.router.navigate(['dashboard', 'editar-cliente', data.id, data]);
  }

  /**
   * Show client info in a modal with an informative form (read-only)
   * @param modal Modal identifier
   * @param data  Cliente entity payload
   */
  public override show(modal: string, data: ClienteModel): void {
    super.show(modal, data);
    const formType = modal === 'delete' ? +FormType.DELETION : +FormType.INFORMATIVE;

    this.form = new ClientesFormDefinition(formType, data as Record<string, string>, null);
  }

  /**
   * Hide a modal and reset transient state related to deletion flows
   * @param modal Modal identifier
   */
  public override hide(modal: string): void {
    this.model = {} as ClienteModel;
    this.canShow[modal] = false;
    this.deleteRequest.reset();
  }


  /**
   * Apply centered alignment to specific columns (kept small and predictable)
   */
  public globalConditionalStyle(_value: string, col: ColInterface): ColStylesInterface {
    if (
      col.field === 'observaciones' ||
      col.field === 'localidad' ||
      col.field === 'usuario_asignado_nombre' ||
      col.field === 'nif'
    ) {
      return { textAlign: 'center' };
    }
    return {};
  }

  /**
   * Visual transforms used by the table:
   * - "observaciones": show "Sí" if present
   * - "nombre": for Agricar (and generally), prefer "razon_social" else "nombre apellidos"
   */
  public globalVisualTransform(value: string, col: ColInterface, row: TreeNode<ClienteModel>): string {
    switch (col.field) {
    case 'observaciones':
      return value ? 'Sí' : '';
    case 'nombre': {
      const cliente = row.data as ClienteModel;

      // Concatenate on demand to keep it cheap; this may run frequently in some deployments (e.g. Agricar)
      return cliente.razon_social != null ? cliente.razon_social : `${cliente.nombre} ${cliente.apellidos ?? ''}`;
    }
    default:
      return value;
    }
  }

  /** Trigger a backend backup export (full clients dataset) */
  public exportarClientes(): void {
    window.location.replace(`${environment.serverUrl}fito_backup/exportar_clientes.php`);
  }

  /**
   * Export the current form-filtered clients as CSV.
   * Uses field names + labels from the form definition to build headers.
   */
  public downloadCsv(): void {
    this.form = new ClientesFormDefinition(
      +FormType.EXPORT,
      this.filtering.value as unknown as Record<string, string>,
      null
    );

    const csvContent = createCsv(
      this.form.fieldNames,
      this.form.fieldLabels,
      this.filtering.value as ClienteModel[]
    );

    // Prefer a pre-existing hidden <a id="csvLink">; if not present, create a temporary one
    let csvLink = document.getElementById('csvLink') as HTMLAnchorElement | null;

    if (!csvLink) {
      csvLink = document.createElement('a');
      csvLink.style.display = 'none';
      document.body.appendChild(csvLink);
    }

    csvLink.download = 'Clientes.csv';
    csvLink.href = encodeURI(csvContent);
    csvLink.click();

    // Clean up if it was created ad-hoc
    if (!csvLink.id) {
      csvLink.remove();
    }
  }

  /** Persist the "show all clients" toggle in session storage (read elsewhere by the table/filter layer) */
  public changeShowAll(): void {
    sessionStorage.setItem('showAllclientes', this.env.showAllClients === true ? 'true' : 'false');
  }

  /** If the deleted client equals the selection in storage, clear it and notify listeners */
  public unsetClientIfSelected(clientId: string): void {
    const clientSelected = StorageManager.getClient();
    
    if (clientSelected?.id === clientId) {
      StorageManager.deleteClient();
      this.dashboard.emitClientChange();
    }
  }

  /**
   * Configure filtering rules depending on current app brand and user.
   * Adds composable filters to the shared `this.filtering` pipeline used by the list-table.
   */
  private initFilter(): void {
    if (this.env.appName === 'benibal' || this.env.appName === 'avafitogest') {
      this.filtering.addFilter((data: TreeNode<ClienteModel>[] | void) =>
        (data || [])
          .filter((it: TreeNode<ClienteModel>) => it)
          .filter((it: TreeNode<ClienteModel>) => {
            if (this.env.appName === 'avafitogest') {
              return true;
            }
            return !this.env.clientesMostrarTodosDefault ? it.data?.acceso_plataforma === 1 : true;
          }
          )
      );
    } else if (this.env.appName === 'agrosalvi') {
      if (this.user.tipo !== 'Agro + Garden') {
        this.filtering.addFilter((data: TreeNode<ClienteModel>[] | void) =>
          (data || [])
            .filter((it: TreeNode<ClienteModel>) => it)
            .filter((it: TreeNode<ClienteModel>) => (!this.env.showAllClients ? this.user.tipo === it.data?.tipo : true))
        );
      }
    } else {
      this.filtering.addFilter((data: TreeNode<ClienteModel>[] | void) =>
        (data || [])
          .filter((it: TreeNode<ClienteModel>) => it)
          .filter((it: TreeNode<ClienteModel>) =>
            !this.env.showAllClients
              ? [it.data?.id_usuario_asignado, it.data?.id_usuario_asignado2].includes(this.user.id)
              : true
          )
      );
    }

    if (this.env.appName === 'innovia' && this.user.rol === 'user') {
      this.filtering.addFilter((data: TreeNode<ClienteModel>[] | void) =>
        (data || []).filter((it: TreeNode<ClienteModel>) => it).filter((it: TreeNode<ClienteModel>) => this.user.id_cliente === it.data?.id)
      );
    }
  }
} // class
